import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerClose } from "../../components/ui/drawer";
import { Button } from "../../components/ui/button";
import { X, Download, Calendar as CalendarIcon, Loader2 } from "lucide-react";
import { useAppStore } from "../../store/appStore";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { formatDisplayDate } from "../../lib/dateFormat";
import { useEffect, useMemo, useRef, useState } from "react";
import { Label } from "../../components/ui/label";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import { cn } from "../../lib/utils";
import apiService from "../../services/apiService";
import { toast } from "react-toastify";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scheduleId: string;
  documentType: 'rental_schedule' | 'power_of_attorney' | 'ownership_acknowledgement' | null;
  schedule?: any; // Allow passing schedule directly
}

function formatLocalYmd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function toDateInputValue(value: unknown): string {
  if (!value) return "";
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return "";
    return formatLocalYmd(value);
  }

  const raw = String(value).trim();
  if (!raw) return "";

  // Already YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    return raw.slice(0, 10);
  }

  // DD-MM-YYYY or DD/MM/YYYY
  const dmy = raw.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
  if (dmy) {
    const day = Number(dmy[1]);
    const month = Number(dmy[2]);
    const year = Number(dmy[3]);
    if (day && month && year) {
      return formatLocalYmd(new Date(year, month - 1, day));
    }
  }

  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return "";
  return formatLocalYmd(date);
}

function parseLocalYmd(value: string): Date | undefined {
  if (!value) return undefined;
  const [y, m, d] = value.split("-").map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
}

function resolveCustomerName(schedule: any, config: any): string {
  const raw =
    config?.customer?.name ??
    config?.customer?.customerName ??
    config?.customer?.legal_name ??
    config?.customer?.legalName ??
    config?.customer?.brandName ??
    config?.customerName ??
    schedule?.customerName ??
    schedule?.customer_name ??
    "";
  if (!raw) return "";
  return String(raw)
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function formatLongDate(ymd: string): string {
  if (!ymd) return "___________________";
  const parsed = parseLocalYmd(ymd);
  if (!parsed) return "___________________";
  return parsed.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function sanitizePdfText(value: unknown): string {
  if (value == null) return "";
  return String(value)
    .replace(/\u00A0/g, " ")
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .normalize("NFKC");
}

/** Collapse extra whitespace for PDF text (newlines/tabs → single space). */
function normalizePdfText(value: string): string {
  return sanitizePdfText(value).replace(/\s+/g, " ").trim();
}

function formatEquipmentDescription(assets: any[]): string {
  if (!assets?.length) return "—";
  return assets
    .map((a) => {
      const item = sanitizePdfText(a?.item);
      const desc = sanitizePdfText(a?.description);
      if (desc) return `${item} - ${desc}`;
      return item;
    })
    .filter(Boolean)
    .join(", ");
}

function getRentalSignedDateValue(schedule: any): unknown {
  return (
    schedule?.rentalSignedDate ??
    schedule?.rental_signed_date ??
    schedule?.config?.rentalSignedDate ??
    schedule?.config?.rental_signed_date ??
    schedule?.config?.assigned_date ??
    schedule?.config?.assignedDate ??
    schedule?.assigned_date ??
    schedule?.assignedDate
  );
}

export default function SchedulePdfDrawer({ open, onOpenChange, scheduleId, documentType, schedule: scheduleProp }: Props) {
  const { schedules, updateSchedule } = useAppStore();
  const scheduleFromStore = schedules.find((s:any) => s.id === scheduleId);
  const schedule = scheduleProp || scheduleFromStore;
  const [assignedDate, setAssignedDate] = useState("");
  const [isSavingAssignedDate, setIsSavingAssignedDate] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const datePickerRef = useRef<HTMLDivElement>(null);
  
  // Extract rawAssets safely before any early returns (for hooks)
  const rawAssets = schedule?.assets || [];
  
  // Group assets by assetLineId and invoiceNo, and sum base, total, and qty
  // This hook must be called before any conditional returns
  const assets = useMemo(() => {
    if (!rawAssets || !Array.isArray(rawAssets) || rawAssets.length === 0) return [];
    
    const grouped = rawAssets.reduce((acc: any, asset: any) => {
      const assetLineId = asset.assetLineId || asset.assetId || 'default';
      const invoiceNo = asset.invoiceNo || 'no-invoice';
      // Create a unique key combining assetLineId and invoiceNo
      const groupKey = `${assetLineId}_${invoiceNo}`;
      const base = asset.basePrice || 0;
      const qty = asset.quantity || 1;
      const total = asset.total || (base * qty * (1 + (asset.gstRate || 0) / 100));
      
      if (!acc[groupKey]) {
        acc[groupKey] = {
          ...asset,
          assetLineId,
          invoiceNo,
          baseSum: 0,
          totalSum: 0,
          qtySum: 0,
        };
      }
      
      acc[groupKey].baseSum += base;
      acc[groupKey].totalSum += total;
      acc[groupKey].qtySum += qty;
      
      return acc;
    }, {});
    
    // Convert grouped object to array and update the asset properties
    return Object.values(grouped).map((groupedAsset: any) => ({
      ...groupedAsset,
      basePrice: groupedAsset.baseSum,
      quantity: groupedAsset.qtySum,
      total: groupedAsset.totalSum,
    }));
  }, [rawAssets]);

  const equipmentDescription = useMemo(
    () => formatEquipmentDescription(assets),
    [assets]
  );

  useEffect(() => {
    if (!open) {
      setIsDatePickerOpen(false);
      return;
    }
    setAssignedDate(toDateInputValue(getRentalSignedDateValue(schedule)));
  }, [
    open,
    scheduleId,
    schedule?.rentalSignedDate,
    schedule?.rental_signed_date,
    schedule?.config?.rentalSignedDate,
    schedule?.config?.rental_signed_date,
    schedule?.config?.assigned_date,
    schedule?.config?.assignedDate,
    schedule?.assigned_date,
    schedule?.assignedDate,
  ]);

  useEffect(() => {
    if (!isDatePickerOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (datePickerRef.current && target && !datePickerRef.current.contains(target)) {
        setIsDatePickerOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [isDatePickerOpen]);

  const applyRentalSignedDateToSchedule = (value: string) => {
    if (!schedule) return;

    // Keep local schedule JSON in sync when previous data was null/empty
    schedule.rentalSignedDate = value;
    schedule.assigned_date = value;
    schedule.assignedDate = value;
    if (schedule.config) {
      schedule.config.rentalSignedDate = value;
    }

    const id = scheduleId || schedule?.id;
    if (id) {
      updateSchedule(id, {
        rentalSignedDate: value,
        assigned_date: value,
        assignedDate: value,
        config: {
          ...schedule.config,
          rentalSignedDate: value,
        },
      } as any);
    }
  };

  const handleAssignedDateChange = async (date: Date | null) => {
    const previous = assignedDate;
    if (!date) {
      setAssignedDate("");
      setIsDatePickerOpen(false);
      return;
    }

    const value = formatLocalYmd(date);
    setAssignedDate(value);
    setIsDatePickerOpen(false);

    const id = scheduleId || schedule?.id;
    if (!id) {
      toast.error("Schedule ID is missing");
      setAssignedDate(previous);
      return;
    }

    setIsSavingAssignedDate(true);
    try {
      await apiService.put("/rental-schedule/update-rental-assigned-date", {
        assigned_date: value,
        scheduleId: id,
      });
      applyRentalSignedDateToSchedule(value);
      toast.success("Assigned date updated");
    } catch (error) {
      console.error("Failed to update assigned date:", error);
      setAssignedDate(previous);
      toast.error("Failed to update assigned date");
    } finally {
      setIsSavingAssignedDate(false);
    }
  };

  if (!open) return null;
  
  if (!schedule || !documentType) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>Document Preview</DrawerTitle>
          </DrawerHeader>
          <div className="p-6 text-center text-muted-foreground">
            {!schedule ? "Schedule not found" : "Document type not specified"}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  const { config, lines } = schedule;
  const ourSignatory = schedule.ourSignatory ?? {
    name: config?.ourSignatory?.name,
    role: config?.ourSignatory?.role,
  };
  const customerSignatory = schedule.customerSignatory ?? {
    name: config?.customerSignatory?.name,
    role: config?.customerSignatory?.role,
  };
  const mraDateStr = config.mraSignedDate ? new Date(config.mraSignedDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '-';
  const effectiveAssignedDate =
    assignedDate || toDateInputValue(getRentalSignedDateValue(schedule));
  const rentalSignedDateStr = formatLongDate(effectiveAssignedDate);
  const formatScheduleNumber = (id: string | number | undefined): string => {
    if (!id) return "";
    const num = typeof id === 'string' ? parseInt(id, 10) : id;
    if (isNaN(num)) return String(id);
    return num < 10 ? `0${num}` : String(num);
  };
  const scheduleNumber = formatScheduleNumber(schedule?.displayId);
  const customerName = resolveCustomerName(schedule, config);

  const handleDownload = () => {
    const doc = new jsPDF('p', 'pt', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 60; // Increased from 40 for more header/footer space
    const headerMargin = 80; // Extra space for header
    const footerMargin = 80; // Extra space for footer
    let yPos = headerMargin;

    // Helper function to add new page if needed
    const checkAddPage = (requiredSpace: number) => {
      if (yPos + requiredSpace > pageHeight - footerMargin) {
        doc.addPage();
        yPos = headerMargin;
        return true;
      }
      return false;
    };

    const renderWrappedText = (
      text: string,
      options?: { isBold?: boolean; lineHeight?: number; paragraphSpacing?: number }
    ) => {
      const { isBold = false, lineHeight = 13, paragraphSpacing = 8 } = options || {};
      const safeText = sanitizePdfText(text);
      const lines = doc.splitTextToSize(safeText, pageWidth - 2 * margin);

      doc.setFont('helvetica', isBold ? 'bold' : 'normal');

      if (!lines.length) {
        yPos += paragraphSpacing;
        return;
      }

      lines.forEach((line: string) => {
        checkAddPage(lineHeight + 2);
        doc.text(line, margin, yPos);
        yPos += lineHeight;
      });

      yPos += paragraphSpacing;
    };

    // Page 1: Main Rental Schedule
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(`RENTAL SCHEDULE No: ${scheduleNumber}`, pageWidth / 2, yPos, { align: 'center' });
    yPos += 30;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    const partiesLine1 = `RENTAL SCHEDULE BETWEEN Astrafin NeoSolutions Private Limited ("Astrafin")`;
    const partiesLine2 = `and ${customerName || "___________________"} ("the Renter") dated ${rentalSignedDateStr}`;
    for (const line of [partiesLine1, partiesLine2]) {
      const wrapped = doc.splitTextToSize(sanitizePdfText(line), pageWidth - 2 * margin);
      doc.text(wrapped, margin, yPos);
      yPos += wrapped.length * 13;
    }
    yPos += 14;
    doc.setFont('helvetica', 'normal');

    const agreementText = `a. The Renter and Astrafin are parties to a Master Rental Agreement dated ${mraDateStr}. Renter offers to rent from Astrafin the Equipment described below. Astrafin agrees to rent the Equipment to Renter at the rental and for the period set out below and subject to the terms and conditions of the Master Rent Agreement.`;
    const splitText = doc.splitTextToSize(agreementText, pageWidth - 2 * margin);
    doc.text(splitText, margin, yPos);
    yPos += splitText.length * 12 + 20;

    const tableContentWidth = pageWidth - 2 * margin;
    const equipmentLabelColWidth = 140;
    const equipmentValueColWidth = tableContentWidth - equipmentLabelColWidth;
    const equipmentCellPadding = 5;
    // Single wrap only — pre-inserting \n + autoTable linebreak caused orphan lines like "seal,"
    const pdfEquipmentDescription = normalizePdfText(equipmentDescription);

    // Equipment Details Table
    const equipmentData = [
      ['Equipment Description', pdfEquipmentDescription],
      ['Commencement Date', new Date(config.commencementDate).toLocaleDateString('en-IN')],
      ['Rental Instalments', 'As per Annexure I'],
      ['Payment Tenure & Term', (() => {
        const frequency = config.frequency?.toLowerCase();
        const isQuarterly = frequency === 'quarterly';
        const frequencyText = isQuarterly ? 'Quarterly' : 'Monthly';
        const frequencyTextLower = isQuarterly ? 'quarter' : 'month';
        const paymentCount = isQuarterly ? Math.floor(config.tenureMonths / 3) : config.tenureMonths;
        return `${paymentCount} ${frequencyText} payments payable at the beginning of the ${frequencyTextLower}`;
      })()],
      ['No of Installments', (() => {
        const frequency = config.frequency?.toLowerCase();
        const isQuarterly = frequency === 'quarterly';
        return isQuarterly ? Math.floor(config.tenureMonths / 3).toString() : config.tenureMonths.toString();
      })()],
      ['Other Terms', '-']
    ];

    autoTable(doc, {
      startY: yPos,
      head: [],
      body: equipmentData,
      theme: 'grid',
      tableWidth: tableContentWidth,
      styles: {
        fontSize: 9,
        cellPadding: equipmentCellPadding,
        overflow: 'linebreak',
        halign: 'left',
        valign: 'top',
      },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: equipmentLabelColWidth },
        1: { cellWidth: equipmentValueColWidth },
      },
      margin: { left: margin, right: margin },
      didParseCell: (data) => {
        if (data.section === 'body' && data.row.index === 0 && data.column.index === 1) {
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(9);
          // Content width = column width minus horizontal padding; wrap once at word ends
          const maxTextWidth = equipmentValueColWidth - equipmentCellPadding * 2;
          data.cell.text = doc.splitTextToSize(
            pdfEquipmentDescription,
            Math.max(20, maxTextWidth)
          );
          // Prevent autoTable from wrapping again (avoids orphan words like "seal,")
          data.cell.styles.overflow = 'hidden';
        }
      },
    });

    yPos = (doc as any).lastAutoTable.finalY + 30;

    // Additional clauses
    const clauses = [
      'Prior Delivery: If the above-mentioned Equipment is delivered to Renter before Astrafin accepts this Rental Schedule, Renter must in any event observe the provisions of the Rental Agreement relating to care, insurance and use of that Equipment as if the rental Agreement was executed.',
      'Authority: Renter irrevocably acknowledges and agrees that the forwarding of this Rental Schedule for acceptance by Astrafin shall constitute an irrevocable authority to Astrafin to pay the supplier of the Equipment referred to in this Rental Schedule.'
    ];

    clauses.forEach(clause => {
      renderWrappedText(clause, { lineHeight: 12, paragraphSpacing: 15 });
    });

    // Signatures
    checkAddPage(120);
    yPos += 20;
    doc.setFont('helvetica', 'bold');
    doc.text('Signed on for and on behalf of Astrafin:', margin, yPos);
    doc.text('Signed on for and on behalf of RENTER:', pageWidth / 2 + 20, yPos);
    yPos += 15;
    
    doc.setFont('helvetica', 'normal');
    doc.text('Astrafin NeoSolutions Private Limited', margin, yPos);
    doc.text(customerName, pageWidth / 2 + 20, yPos);
    yPos += 100;
    
    doc.text('Signature: ___________________', margin, yPos);
    doc.text('Signature: ___________________', pageWidth / 2 + 20, yPos);
    yPos += 25;
    
    doc.text(`Capacity: ${ourSignatory?.role || ''}`, margin, yPos);
    doc.text(`Capacity: ${customerSignatory?.role || ''}`, pageWidth / 2 + 20, yPos);
    yPos += 15;
    
    doc.text(`Name: ${ourSignatory?.name || ''}`, margin, yPos);
    doc.text(`Name: ${customerSignatory?.name || ''}`, pageWidth / 2 + 20, yPos);

    // Page 2: ANNEXURE 1 - Rental Schedule Table
    doc.addPage();
      yPos = headerMargin;
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(`ANNEXURE 1 TO RENTAL SCHEDULE NO: ${scheduleNumber}`, pageWidth / 2, yPos, { align: 'center' });
    yPos += 30;

    const rentalTableData = lines.map((line:any) => [
      formatDisplayDate(line.fromDate),
      formatDisplayDate(line.toDate),
      line.periodNo.toString(),
      formatDisplayDate(line.dueDate),
      line.baseRentRounded.toLocaleString('en-IN'),
      (line.gstRaw ?? 0).toLocaleString('en-IN'),
      (line.baseRentRounded + (line.gstRaw ?? 0)).toLocaleString('en-IN')
    ]);

    autoTable(doc, {
      startY: yPos,
      head: [['From Date', 'To Date', 'Installment No', 'Due Date', 'Base Rentals in INR', 'GST', 'Total']],
      body: rentalTableData,
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 3, halign: 'center' },
      headStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold' },
      columnStyles: {
        4: { halign: 'center' },
        5: { halign: 'center' },
        6: { halign: 'center' }
      },
      margin: { top: headerMargin, bottom: footerMargin, left: margin, right: margin },
     
    });

    // Page 3: ASSET ANNEXURE
    doc.addPage();
      yPos = headerMargin;
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(`ASSET ANNEXURE OF RENTAL SCHEDULE NO. ${scheduleNumber}`, pageWidth / 2, yPos, { align: 'center' });
    yPos += 30;

    let totalAssetAmount = 0;
    const assetTableData = assets.map((asset:any, index:number) => {
      const amount = (asset.total || 0);
      totalAssetAmount += amount;
      return [
        (index + 1).toString(),
        asset.vendorName || '-',
        asset.invoiceNo || '-',
        asset.invoiceDate ? new Date(asset.invoiceDate).toLocaleDateString('en-IN') : '-',
        asset.hsn || '-',
        asset.item || '-',
        asset.description ? asset.description : '-',
        (asset.quantity || 1).toString(),
        asset.deliveryAddress || '-',
        Math.round(Number(amount) || 0).toLocaleString('en-IN')
      ];
    });

    assetTableData.push([
      '', '', '', '', '', '', '', '', 'Total', totalAssetAmount.toLocaleString('en-IN')
    ]);

    autoTable(doc, {
      startY: yPos,
      head: [['Sr. No.', 'Vendor Name', 'Invoice No', 'Invoice Date', 'HSN Code', 'Asset Item', 'Asset Description', 'Qty', 'Delivery Address', 'Amount (In INR)']],
      body: assetTableData,
      theme: 'grid',
      styles: { fontSize: 7, cellPadding: 2, halign: 'center' },
      headStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 7 },
      columnStyles: {
        0: { cellWidth: 25, halign: 'center' },
        1: { halign: 'center' },
        2: { halign: 'center' },
        3: { halign: 'center' },
        4: { halign: 'center' },
        5: { halign: 'center' },
        6: { halign: 'center' },
        7: { halign: 'center' },
        8: { cellWidth: 100, halign: 'center' },
        9: { halign: 'center', fontStyle: 'bold' }
      },
      margin: { left: 30, right: 30 }
    });

    yPos = (doc as any).lastAutoTable.finalY + 40;

    // Asset Annexure Signatures
    checkAddPage(120);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Signed on for and on behalf of Astrafin:', margin, yPos);
    doc.text('Signed on for and on behalf of RENTER:', pageWidth / 2 + 20, yPos);
    yPos += 15;
    
    doc.setFont('helvetica', 'normal');
    doc.text('Astrafin NeoSolutions Private Limited', margin, yPos);
    doc.text(customerName, pageWidth / 2 + 20, yPos);
    yPos += 100;
    
    doc.text('Signature: ___________________', margin, yPos);
    doc.text('Signature: ___________________', pageWidth / 2 + 20, yPos);
    yPos += 25;
    
    doc.text(`Capacity: ${ourSignatory?.role || ''}`, margin, yPos);
    doc.text(`Capacity: ${customerSignatory?.role || ''}`, pageWidth / 2 + 20, yPos);
    yPos += 15;
    
    doc.text(`Name: ${ourSignatory?.name || ''}`, margin, yPos);
    doc.text(`Name: ${customerSignatory?.name || ''}`, pageWidth / 2 + 20, yPos);

    // Page 4: ANNEXURE II - Certificate of Acceptance
    doc.addPage();
      yPos = headerMargin;
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('ANNEXURE II: CERTIFICATE OF ACCEPTANCE', pageWidth / 2, yPos, { align: 'center' });
    yPos += 40;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const certText = [
      `Renter Name: ${customerName}   MRA Dated ${mraDateStr}`,
      `Rental Schedule No – ${scheduleNumber}`,
      '',
      'We hereby certify that the Equipment detailed in the Rent Schedule mentioned above was delivered and duly accepted by us and they are in satisfactory working order.'
    ];

    certText.forEach(text => {
      const splitCertText = doc.splitTextToSize(text, pageWidth - 2 * margin);
      doc.text(splitCertText, margin, yPos);
      yPos += splitCertText.length * 15 + 10;
    });

    yPos += 30;

    // Certificate Signatures
    doc.setFont('helvetica', 'bold');
    doc.text('Signed on for and on behalf of Astrafin:', margin, yPos);
    doc.text('Signed on for and on behalf of RENTER:', pageWidth / 2 + 20, yPos);
    yPos += 15;
    
    doc.setFont('helvetica', 'normal');
    doc.text('Astrafin NeoSolutions Private Limited', margin, yPos);
    doc.text(customerName, pageWidth / 2 + 20, yPos);
    yPos += 100;
    
    doc.text('Signature: ___________________', margin, yPos);
    doc.text('Signature: ___________________', pageWidth / 2 + 20, yPos);
    yPos += 25;
    
    doc.text(`Capacity: ${ourSignatory?.role || ''}`, margin, yPos);
    doc.text(`Capacity: ${customerSignatory?.role || ''}`, pageWidth / 2 + 20, yPos);
    yPos += 15;
    
    doc.text(`Name: ${ourSignatory?.name || ''}`, margin, yPos);
    doc.text(`Name: ${customerSignatory?.name || ''}`, pageWidth / 2 + 20, yPos);

    // Page 5: Acknowledgement of Ownership
    doc.addPage();
      yPos = headerMargin;
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('ACKNOWLEDGEMENT OF OWNERSHIP AND', pageWidth / 2, yPos, { align: 'center' });
    yPos += 15;
    doc.text('ACKNOWLEDGEMENT OF DELIVERY & AUTHORITY TO PAY', pageWidth / 2, yPos, { align: 'center' });
    yPos += 30;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const ackText = [
      `In respect of the Master Rent Agreement (MRA) dated ${mraDateStr}, the Rental Schedule No. ${scheduleNumber} dated ${rentalSignedDateStr} the equipment referred to below we request ${customerName} ("the Renter") to HEREBY FURTHER ACKNOWLEDGE that under the terms of the aforesaid Master Rent Agreement ownership of the equipment will remain with Astrafin during the term and upon expiration and/ or termination of the said Rental Agreement.`,
      '',
      'Rental Agreement',
      `MRA DATE: ${mraDateStr}`,
      'Owner: Astrafin NeoSolutions Private Limited',
      `Renter: ${customerName}`,
      `Rental Schedule No. ${scheduleNumber} dated ${rentalSignedDateStr}`,
      `Goods/Equipment: ${normalizePdfText(equipmentDescription)}`,
      '',
      'It is also hereby declared by the Renter:',
      '1. That delivery / installation of the equipment has been completed and they are in satisfactory working order.',
      '2. That the equipment is in accordance with your request to Astrafin.',
      '3. That the Equipment/s as mentioned herein is insured in accordance with the MRA.',
      '4. The Equipment /Goods as mentioned herein are new /Refurbished and the invoices are in the name of Astrafin NeoSolutions Private Limited'
    ];

    ackText.forEach(text => {
      const isHeading = text === '' || text.startsWith('Rental Agreement') || text.startsWith('It is also');
      const paragraphSpacing = text === ''
        ? 4
        : text.startsWith('Goods/Equipment:')
          ? 2
          : 8;
      renderWrappedText(text, { isBold: isHeading, lineHeight: 13, paragraphSpacing });
    });

    yPos += 30;

    // Final Signatures
    checkAddPage(100);
    doc.setFont('helvetica', 'bold');
    doc.text('For Astrafin NeoSolutions Private Limited', margin, yPos);
    doc.text(`For ${customerName}`, pageWidth / 2 + 20, yPos);
    yPos += 100;
    doc.setFont('helvetica', 'normal');
    doc.text('Signature: ___________________', margin, yPos);
    doc.text('Signature: ___________________', pageWidth / 2 + 20, yPos);
    yPos += 25;
    
    doc.text(`Capacity: ${ourSignatory?.role || ''}`, margin, yPos);
    doc.text(`Capacity: ${customerSignatory?.role || ''}`, pageWidth / 2 + 20, yPos);
    yPos += 15;
    
    doc.text(`Name: ${ourSignatory?.name || ''}`, margin, yPos);
    doc.text(`Name: ${customerSignatory?.name || ''}`, pageWidth / 2 + 20, yPos);

    // Save the PDF
    doc.save(`Rental_Schedule_${scheduleNumber}.pdf`);
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange} handleOnly>
      <DrawerContent className="h-[90vh] overflow-visible" showCloseButton={false}>
        <DrawerHeader className="border-b overflow-visible relative z-[110]">
          <div className="flex items-center justify-between gap-4 overflow-visible">
            <DrawerTitle>Rental Schedule No: {scheduleNumber}</DrawerTitle>
            <div className="flex items-end gap-3 shrink-0 overflow-visible">
              <div ref={datePickerRef} className="relative flex flex-col gap-1 text-left shrink-0">
                <Label className="text-sm whitespace-nowrap">Assign Date</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={isSavingAssignedDate}
                  className={cn(
                    "h-9 w-[180px] justify-start text-left font-normal",
                    !assignedDate && "text-muted-foreground"
                  )}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsDatePickerOpen((prev) => !prev);
                  }}
                >
                  {isSavingAssignedDate ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <CalendarIcon className="mr-2 h-4 w-4" />
                  )}
                  {assignedDate
                    ? format(parseLocalYmd(assignedDate) as Date, "dd MMM yyyy")
                    : "Select date"}
                </Button>

                {isDatePickerOpen && (
                  <div
                    className="absolute right-0 top-full z-[120] mt-2 rounded-md border bg-white p-2 shadow-lg"
                    onPointerDown={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <DatePicker
                      selected={parseLocalYmd(assignedDate)}
                      onChange={handleAssignedDateChange}
                      inline
                      calendarClassName="!border-0 !shadow-none"
                    />
                  </div>
                )}
              </div>
              <Button variant="outline" size="sm" onClick={handleDownload}>
                <Download className="h-4 w-4 mr-2" />
                Download PDF
              </Button>
              <DrawerClose asChild>
                <Button variant="ghost" size="icon">
                  <X className="h-4 w-4" />
                </Button>
              </DrawerClose>
            </div>
          </div>
        </DrawerHeader>

        <div className="flex-1 overflow-auto p-6 bg-muted/30">
          <div className="max-w-4xl mx-auto bg-background border rounded-lg p-12 shadow-sm space-y-8">
            {/* Main Rental Schedule */}
            <div className="space-y-6">
              <div className="text-center">
                <h1 className="text-2xl font-bold">RENTAL SCHEDULE No: {scheduleNumber}</h1>
              </div>

              <div className="space-y-4 text-sm">
                <p className="font-semibold leading-relaxed">
                  RENTAL SCHEDULE BETWEEN Astrafin NeoSolutions Private Limited ("Astrafin")
                  <br />
                  and {customerName || "___________________"} ("the Renter") dated{" "}
                  {rentalSignedDateStr}
                </p>

                <p>
                  a. The Renter and Astrafin are parties to a Master Rental Agreement dated {mraDateStr}. 
                  Renter offers to rent from Astrafin the Equipment described below. Astrafin agrees to rent the Equipment to Renter at the rental and 
                  for the period set out below and subject to the terms and conditions of the Master Rent Agreement.
                </p>

                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <tbody>
                      <tr className="border-b">
                        <td className="p-3 font-semibold bg-muted w-1/3 align-top">Equipment Description</td>
                        <td className="p-3 align-top whitespace-normal break-words">
                          {equipmentDescription}
                        </td>
                      </tr>
                      <tr className="border-b">
                        <td className="p-3 font-semibold bg-muted">Commencement Date</td>
                        <td className="p-3">{new Date(config.commencementDate).toLocaleDateString('en-IN')}</td>
                      </tr>
                      <tr className="border-b">
                        <td className="p-3 font-semibold bg-muted">Rental Instalments</td>
                        <td className="p-3">As per Annexure I</td>
                      </tr>
                      <tr className="border-b">
                        <td className="p-3 font-semibold bg-muted">Payment Tenure & Term</td>
                        <td className="p-3">{(() => {
                          const frequency = config.frequency?.toLowerCase();
                          const isQuarterly = frequency === 'quarterly';
                          const frequencyText = isQuarterly ? 'Quarterly' : 'Monthly';
                          const frequencyTextLower = isQuarterly ? 'quarter' : 'month';
                          const paymentCount = isQuarterly ? Math.floor(config.tenureMonths / 3) : config.tenureMonths;
                          return `${paymentCount} ${frequencyText} payments payable at the beginning of the ${frequencyTextLower}`;
                        })()}</td>
                      </tr>
                      <tr className="border-b">
                        <td className="p-3 font-semibold bg-muted">No of Installments</td>
                        <td className="p-3">{(() => {
                          const frequency = config.frequency?.toLowerCase();
                          const isQuarterly = frequency === 'quarterly';
                          return isQuarterly ? Math.floor(config.tenureMonths / 3) : config.tenureMonths;
                        })()}</td>
                      </tr>
                      <tr>
                        <td className="p-3 font-semibold bg-muted">Other Terms</td>
                        <td className="p-3">-</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <p className="text-xs pt-4">
                  <span className="font-semibold">Prior Delivery:</span> If the above-mentioned Equipment is delivered to Renter before Astrafin accepts this Rental Schedule, 
                  Renter must in any event observe the provisions of the Rental Agreement relating to care, insurance and use of that Equipment as if the rental Agreement was executed.
                </p>

                <p className="text-xs">
                  <span className="font-semibold">Authority:</span> Renter irrevocably acknowledges and agrees that the forwarding of this Rental Schedule for acceptance by Astrafin 
                  shall constitute an irrevocable authority to Astrafin to pay the supplier of the Equipment referred to in this Rental Schedule.
                </p>

                {/* Signatures */}
                <div className="grid grid-cols-2 gap-8 pt-8">
                  <div className="space-y-2">
                    <p className="font-semibold text-xs">Signed on for and on behalf of Astrafin:</p>
                    <p className="text-xs">Astrafin NeoSolutions Private Limited</p>
                    <div className="pt-12 space-y-1">
                      <p className="text-xs">Signature: ___________________</p>
                      <p className="text-xs">Capacity: {ourSignatory?.role || ''}</p>
                      <p className="text-xs">Name: {ourSignatory?.name || ''}</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <p className="font-semibold text-xs">Signed on for and on behalf of RENTER:</p>
                    <p className="text-xs">{customerName}</p>
                    <div className="pt-12 space-y-1">
                      <p className="text-xs">Signature: ___________________</p>
                      <p className="text-xs">Capacity: {customerSignatory?.role || ''}</p>
                      <p className="text-xs">Name: {customerSignatory?.name || ''}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ANNEXURE 1 */}
            <div className="page-break space-y-6 pt-12 border-t-2">
              <div className="text-center">
                <h2 className="text-xl font-bold">ANNEXURE 1 TO RENTAL SCHEDULE NO: {scheduleNumber}</h2>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-xs border">
                  <thead>
                    <tr className="bg-muted">
                      <th className="p-2 border text-center">From Date</th>
                      <th className="p-2 border text-center">To Date</th>
                      <th className="p-2 border text-center">Installment No</th>
                      <th className="p-2 border text-center">Due Date</th>
                      <th className="p-1 border text-center">Base Rentals in INR</th>
                      <th className="p-2 border text-center">GST</th>
                      <th className="p-2 border text-center">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((line:any) => (
                      line.periodNo != 'i' && (
                      <tr key={line.scheduleLineId}>
                        <td className="p-2 border text-center">{new Date(line.fromDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })}</td>
                        <td className="p-2 border text-center">{new Date(line.toDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })}</td>
                        <td className="p-2 border text-center">{line.periodNo}</td>
                        <td className="p-2 border text-center">{new Date(line.dueDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })}</td>
                        <td className="p-1 border text-center">{line.baseRentRounded.toLocaleString('en-IN')}</td>
                        <td className="p-2 border text-center">{line.gstRaw.toLocaleString('en-IN')}</td>
                        <td className="p-2 border text-center">{(line.baseRentRounded + (line.gstRaw ?? 0)).toLocaleString('en-IN')}</td>
                      </tr>
                      )
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ASSET ANNEXURE */}
            <div className="page-break space-y-6 pt-12 border-t-2">
              <div className="text-center">
                <h2 className="text-xl font-bold">ASSET ANNEXURE OF RENTAL SCHEDULE NO. {scheduleNumber}</h2>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-xs border">
                  <thead>
                    <tr className="bg-muted">
                      <th className="p-2 border">Sr. No.</th>
                      <th className="p-2 border">Vendor Name</th>
                      <th className="p-2 border">Invoice No</th>
                      <th className="p-2 border">Invoice Date</th>
                      <th className="p-2 border">HSN Code</th>
                      <th className="p-2 border">Asset Item</th>
                      <th className="p-2 border">Asset Description</th>
                      <th className="p-2 border">Qty</th>
                      <th className="p-2 border">Delivery Address</th>
                      <th className="p-2 border">Amount (In INR)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assets.map((asset:any, index:number) => {
                      return (
                        <tr key={asset.assetLineId || asset.assetId}>
                          <td className="p-2 border text-center">{index + 1}</td>
                          <td className="p-2 border">{asset.vendorName || '-'}</td>
                          <td className="p-2 border">{asset.invoiceNo || '-'}</td>
                          <td className="p-2 border text-center">{asset.invoiceDate ? new Date(asset.invoiceDate).toLocaleDateString('en-IN') : '-'}</td>
                          <td className="p-2 border text-center">{asset.hsn || '-'}</td>
                          <td className="p-2 border">{asset.item || '-'}</td>
                          <td className="p-2 border">{asset.description ? asset.description : '-'}</td>
                          <td className="p-2 border text-center">{asset.quantity || 1}</td>
                          <td className="p-2 border">{asset.deliveryAddress || '-'}</td>
                          <td className="p-2 border text-right">{Math.round(Number(asset.total) || 0).toLocaleString('en-IN')}</td>
                        </tr>
                      );
                    })}
                    <tr className="font-bold">
                      <td colSpan={9} className="p-2 border text-right">Total</td>
                      <td className="p-2 border text-right">
                        {assets.reduce((sum:number, asset:any) => sum + (asset.total), 0).toLocaleString('en-IN')}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Asset Annexure Signatures */}
              <div className="grid grid-cols-2 gap-8 pt-8">
                <div className="space-y-2">
                  <p className="font-semibold text-xs">Signed on for and on behalf of Astrafin:</p>
                  <p className="text-xs">Astrafin NeoSolutions Private Limited</p>
                  <div className="pt-12 space-y-1">
                    <p className="text-xs">Signature: ___________________</p>
                    <p className="text-xs">Capacity: {ourSignatory?.role || ''}</p>
                    <p className="text-xs">Name: {ourSignatory?.name || ''}</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <p className="font-semibold text-xs">Signed on for and on behalf of RENTER:</p>
                  <p className="text-xs">{customerName}</p>
                  <div className="pt-12 space-y-1">
                    <p className="text-xs">Signature: ___________________</p>
                    <p className="text-xs">Capacity: {customerSignatory?.role || ''}</p>
                    <p className="text-xs">Name: {customerSignatory?.name || ''}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* ANNEXURE II - Certificate of Acceptance */}
            <div className="page-break space-y-6 pt-12 border-t-2">
              <div className="text-center">
                <h2 className="text-xl font-bold">ANNEXURE II: CERTIFICATE OF ACCEPTANCE</h2>
              </div>

              <div className="space-y-4 text-sm">
                <p>Renter Name: <span className="font-semibold">{customerName}</span></p>
                <p>MRA Dated {mraDateStr}</p>
                <p>Rental Schedule No – {scheduleNumber}</p>
                <p className="pt-4">
                  We hereby certify that the Equipment detailed in the Rent Schedule mentioned above was delivered and duly accepted by us and they are in satisfactory working order.
                </p>

                {/* Certificate Signatures */}
                <div className="grid grid-cols-2 gap-8 pt-8">
                  <div className="space-y-2">
                    <p className="font-semibold text-xs">Signed on for and on behalf of Astrafin:</p>
                    <p className="text-xs">Astrafin NeoSolutions Private Limited</p>
                    <div className="pt-12 space-y-1">
                      <p className="text-xs">Signature: ___________________</p>
                      <p className="text-xs">Capacity: {ourSignatory?.role || ''}</p>
                      <p className="text-xs">Name: {ourSignatory?.name || ''}</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <p className="font-semibold text-xs">Signed on for and on behalf of RENTER:</p>
                    <p className="text-xs">{customerName}</p>
                    <div className="pt-12 space-y-1">
                      <p className="text-xs">Signature: ___________________</p>
                      <p className="text-xs">Capacity: {customerSignatory?.role || ''}</p>
                      <p className="text-xs">Name: {customerSignatory?.name || ''}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Acknowledgement of Ownership */}
            <div className="page-break space-y-6 pt-12 border-t-2">
              <div className="text-center">
                <h2 className="text-lg font-bold">ACKNOWLEDGEMENT OF OWNERSHIP AND<br />ACKNOWLEDGEMENT OF DELIVERY & AUTHORITY TO PAY</h2>
              </div>

              <div className="space-y-4 text-sm">
                <p>
                  In respect of the Master Rent Agreement (MRA) dated {mraDateStr}, 
                  the Rental Schedule No. {scheduleNumber} dated {rentalSignedDateStr} the equipment 
                  referred to below we request {customerName} ("the Renter") to HEREBY FURTHER ACKNOWLEDGE that under the terms of the aforesaid Master Rent Agreement 
                  ownership of the equipment will remain with Astrafin during the term and upon expiration and/ or termination of the said Rental Agreement.
                </p>

                <div className="space-y-2 pt-4">
                  <p className="font-semibold">Rental Agreement</p>
                  <p>MRA DATE: {mraDateStr}</p>
                  <p>Owner: Astrafin NeoSolutions Private Limited</p>
                  <p>Renter: {customerName}</p>
                  <p>Rental Schedule No. {scheduleNumber} dated {rentalSignedDateStr}</p>
                  <p className="break-words whitespace-normal">Goods/Equipment: {equipmentDescription}</p>
                </div>

                <div className="space-y-2 pt-4">
                  <p className="font-semibold">It is also hereby declared by the Renter:</p>
                  <ol className="list-decimal list-inside space-y-1 pl-4">
                    <li>That delivery / installation of the equipment has been completed and they are in satisfactory working order.</li>
                    <li>That the equipment is in accordance with your request to Astrafin.</li>
                    <li>That the Equipment/s as mentioned herein is insured in accordance with the MRA.</li>
                    <li>The Equipment /Goods as mentioned herein are new /Refurbished and the invoices are in the name of Astrafin NeoSolutions Private Limited</li>
                  </ol>
                </div>

                {/* Final Signatures */}
                <div className="grid grid-cols-2 gap-8 pt-8">
                  <div className="space-y-2">
                    <p className="font-semibold text-xs">For Astrafin NeoSolutions Private Limited</p>
                    <div className="pt-12 space-y-1">
                      <p className="text-xs">Signature: ___________________</p>
                      <p className="text-xs">Capacity: {ourSignatory?.role || ''}</p>
                      <p className="text-xs">Name: {ourSignatory?.name || ''}</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <p className="font-semibold text-xs">For {customerName}</p>
                    <div className="pt-12 space-y-1">
                      <p className="text-xs">Signature: ___________________</p>
                      <p className="text-xs">Capacity: {customerSignatory?.role || ''}</p>
                      <p className="text-xs">Name: {customerSignatory?.name || ''}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}